/* ============================================================================
   olmia-filters.js — collectiepagina: dynamische spec-filters per categorie,
   sort, chips, mobiele drawer. Ported one-to-one from the design's filters.js,
   but driven by the SERVER-RENDERED .pcard cards (data-product / data-specs)
   instead of a global product catalog.
   De filter-set volgt de collectie (cobots payload/reikwijdte, AMR een
   IP-rating, camera's niet). Het categorieblok verschijnt op de all-view.

   ── FILTEREN OVER DE HELE COLLECTIE (2026-08-20) ───────────────────────────
   Het statische design laadde ALLE producten in één keer en filterde daar
   client-side overheen. Deze Shopify-poort pagineert serverzijdig (24 per
   pagina), en dit script las alleen de GERENDERDE kaarten. Gevolg, gemeten op
   /collections/alle-producten: de teller meldde 24 waar de collectie er 177
   heeft, de facetten telden binnen pagina 1 (cobots 9 i.p.v. 34, grippers 4
   i.p.v. 28) en vier hele categorieën — wisselsystemen (48), service-kits (42),
   boosters-supports (9) en software (3), samen 102 van de 177 producten —
   kwamen in het categoriefilter helemaal niet voor. Precies de klacht van Olmia:
   een product met een productpagina mag nooit ontbreken op een collectiepagina.

   Alles serverzijdig renderen kan niet: Liquid levert nooit meer dan 50
   producten per render (gemeten: `limit: 250` -> 50, `offset: 50` -> 0) en
   `paginate` staat maximaal 50 per pagina toe. Daarom HYDRATEERT dit script:

     1. De server rendert pagina N zoals altijd (snelle eerste weergave, LCP
        ongewijzigd) en zet het ECHTE aantal al in de teller — die klopt dus ook
        zonder JavaScript.
     2. Dit script haalt de overige pagina's na via de Section Rendering API
        (`?page=N&sections=<section.id>`) en bewaart per product de serverzijdig
        gerenderde kaart-HTML plus zijn data-product/data-specs. Zelfde snippet,
        zelfde serverzijdige prijsmuur — er wordt nergens een tweede
        prijsoppervlak gemaakt en er wordt hier niets aan een bedrag gerekend.
     3. Pas dan bouwt hij de zijbalk. De facetlijst en de facetaantallen gaan
        daarmee over de HELE collectie en komen per definitie overeen met wat je
        krijgt als je erop klikt.
     4. Een nagehaalde kaart komt PAS in de DOM als een filter hem toont (elOf).
        Gemeten op mobiel: de landingsstand houdt daardoor 974 DOM-knopen, net
        als voorheen; alles ineens inhangen maakte er 4.296 van.

   TWEE STANDEN
     • paginastand — geen enkel filter/zoekterm/sortering actief én de server
       heeft gepagineerd: de serverpagina blijft staan, de paginatienav blijft
       zichtbaar, de teller toont het collectietotaal. Dit is de landingsstand,
       en hij kost precies evenveel als voorheen.
     • volledige stand — zodra er gefilterd, gezocht of gesorteerd wordt: alle
       treffers uit de hele collectie worden getoond en de paginatienav verdwijnt
       (er valt niets meer te bladeren), exact zoals het design het deed.
   Mislukt het nahalen, dan blijft de paginatienav altijd staan zodat geen enkel
   product onbereikbaar wordt.

   ── EEN ONVOLLEDIGE LIJST IS GEEN TELLING (2026-08-20) ─────────────────────
   Een pagina die niet binnenkwam werd stilzwijgend uit de dataset gelaten en de
   zijbalk werd tóch gebouwd. Gemeten (pagina 5 van 8 kunstmatig laten falen):
   teller bleef 177, Cobot-armen zakte van 34 naar 18, Service & kits van 42
   naar 35, som van de facetten 153. De pagina sprak zichzelf dan tegen, en
   klikken op "Cobot-armen (18)" maakte 16 cobots onbereikbaar — precies de
   klacht van Olmia in een nieuwe jas. De aanleiding is niet theoretisch: deze
   winkel gaf in de week van 18 augustus herhaaldelijk HTTP 429.

   Vanaf nu geldt: EEN FACETAANTAL IS EEN BEWERING OVER DE HELE COLLECTIE.
     1. elke gefaalde pagina krijgt één herkansing, serieel en na een pauze
        (RETRY_DELAY_MS) — drie tegelijk opnieuw afvuren lokt dezelfde 429 uit;
     2. lukt dat óók niet, dan is `complete` false en toont de zijbalk GEEN
        aantallen meer: alleen labels. Filteren blijft werken, maar het scherm
        belooft geen getal dat het niet kan waarmaken;
     3. de paginanavigatie blijft staan (setPager) en #collPartial legt in het
        Nederlands uit waarom er minder staat dan de teller belooft.
   Liever geen getal dan een te laag getal.

   Editor-safe (see olmia-editor.js): all state and DOM lookups are built inside
   init(root), scoped to the re-rendered section root; the sidebar is rebuilt and
   the delegated listeners are guarded (data-filters-bound) so a re-render rebinds
   the fresh nodes without stacking handlers. De hydratie hangt aan één
   AbortController per sectiewortel en wordt in teardown() afgebroken; opgehaalde
   pagina's leven in een modulecache, zodat een herrender in de theme-editor niet
   opnieuw het net op gaat.
   ========================================================================== */
(function () {
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  /* Bovengrens op het nahalen. 177 producten = 8 pagina's; deze grens (≈960
     producten) beschermt tegen een catalogus die ooit veel groter wordt zonder
     dat iemand dit bestand nog leest. Wordt hij overschreden, dan blijft de
     paginatienav staan en filtert het script binnen wat er staat — dan is
     serverzijdig filteren (Shopify Search & Discovery) de juiste volgende stap. */
  const MAX_HYDRATE_PAGES = 40;
  /* Eén herkansing per gefaalde pagina, na een pauze. De aanleiding is niet
     theoretisch: deze winkel gaf in de week van 18 augustus herhaaldelijk HTTP
     429 op parallelle verzoeken. De herkansing loopt bewust SERIEEL en na een
     wachttijd — nog eens drie tegelijk afvuren is precies wat de 429 uitlokte. */
  const RETRY_DELAY_MS = 700;
  /* Nagehaalde pagina-HTML per URL. Overleeft een sectie-herrender in de
     theme-editor, zodat die niet bij elke instellingswijziging 7 verzoeken doet. */
  const PAGE_CACHE = new Map();
  /* Lopende hydratie per sectiewortel, zodat teardown() hem kan afbreken.
     WeakMap, niet Map: de theme-editor vervangt de sectiewortel bij elke
     herrender, en een gewone Map zou elke oude, losgekoppelde wortel voor de
     rest van de sessie in leven houden. */
  const RUNNING = new WeakMap();

  function init(sectionRoot) {
    const scope = sectionRoot || document;
    const grid = scope.querySelector('#grid');
    /* De WRAPPER VAN DIT RASTER, niet zomaar de eerste [data-collection] in beeld.
       sections/campaign-hero.liquid zet op merk- en campagnepagina's zelf een
       `data-collection="<handle>"` neer, en die staat VÓÓR deze sectie. Met
       querySelector pakte dit script daar de hero, die geen data-cat-labels en
       geen data-collection-handle draagt: op alle tien merkpagina's viel het
       categoriefilter daardoor terug op de vertitelde tag — "Cobots" i.p.v.
       "Cobot-armen", "Service kits" i.p.v. "Service & kits", "Accessories"
       i.p.v. "Toebehoren" — en werd de pagina als all-view behandeld.
       closest() kan die fout per constructie niet maken. */
    const root = (grid && grid.closest('[data-collection]')) || scope.querySelector('[data-collection]');
    if (!grid || !root) return;

    const params = new URLSearchParams(location.search);
    const handle = root.dataset.collectionHandle || '';
    let catLabels = {};
    try { catLabels = JSON.parse(root.dataset.catLabels || '{}'); } catch (e) { catLabels = {}; }

    /* serverfeiten uit main-collection.liquid (paginate.*) */
    const serverTotal = parseInt(grid.dataset.total, 10);
    const pageCount = Math.max(1, parseInt(grid.dataset.pages, 10) || 1);
    const curPage = Math.max(1, parseInt(grid.dataset.page, 10) || 1);
    const sectionId = grid.dataset.sectionId || '';

    /* filter-config per collectie (per-category facet sets + default facet set).
       Only the categories with a SPEC-driven facet set need an entry: an unknown
       category falls back to the design's own default (brand + price), exactly
       like activeKeys() in the design's filters.js. */
    const CF = {
      cobots:      ['brand', 'payload', 'reach', 'ip', 'price'],
      solutions:   ['brand', 'payload', 'price'],
      amr:         ['brand', 'payload', 'ip', 'price'],
      grippers:    ['brand', 'price'],
      vision:      ['brand', 'price'],
      sensors:     ['brand', 'price'],
      accessories: ['brand', 'price'],
    };
    const CF_DEFAULT = ['brand', 'price'];
    // all-products view = design's multi-category branch: category + brand + payload + reach + price
    const DEFAULT_FILTERS = ['category', 'brand', 'payload', 'reach', 'price'];
    /* Design order for the seven categories the design shipped with. This is a
       PREFERRED ORDER, not the catalogue: the facet is derived from the cat:
       tags actually present on the rendered cards, so a category added later
       (wisselsystemen, service-kits, …) shows up on its own, appended after the
       seven known ones. Labels come from Liquid (data-cat-labels = every
       collection in the store, handle -> title). */
    const CAT_ORDER = ['solutions', 'cobots', 'grippers', 'vision', 'amr', 'sensors', 'accessories'];

    /* Eén product in design-vorm. `el` is de ECHTE kaart in het raster; voor een
       nagehaald product is die er nog niet en houdt `html` de serverzijdig
       gerenderde kaart vast tot een filter hem nodig heeft (zie elOf). Zo blijft
       de landingsstand precies even zwaar als voorheen: 24 kaarten in de DOM,
       terwijl er wél over alle 177 gefilterd en geteld wordt. */
    function record(el, product, specs, html) {
      const p = product || {};
      return {
        el: el || null, html: html || '', specs: specs || null,
        name: p.title || '',
        brand: p.brand || '',
        category: p.category || '',
        price: +p.price || 0,
        payloadKg: (p.payload === null || p.payload === undefined) ? null : +p.payload,
        reachMm: (p.reach === null || p.reach === undefined) ? null : +p.reach,
      };
    }
    function parseCard(el) {
      let product = {}, specs = null;
      try { product = JSON.parse(el.dataset.product || '{}'); } catch (e) { product = {}; }
      try { specs = el.dataset.specs ? JSON.parse(el.dataset.specs) : null; } catch (e) { specs = null; }
      return { product, specs };
    }
    /* read the server-rendered cards into design-shaped product objects */
    function readCards() {
      return $$('.pcard', grid).map(el => { const { product, specs } = parseCard(el); return record(el, product, specs, ''); });
    }

    /* Maakt de kaart van een nagehaald product alsnog aan — pas op het moment
       dat een filter hem daadwerkelijk toont. De HTML komt ONGEWIJZIGD van de
       server (zelfde snippet, zelfde prijsmuur); hier wordt niets aan prijs of
       inhoud gerekend. */
    let freshEls = [];
    function elOf(p) {
      if (p.el) return p.el;
      const tpl = document.createElement('template');
      tpl.innerHTML = p.html;
      p.el = tpl.content.firstElementChild;
      if (p.el) freshEls.push(p.el);
      return p.el;
    }

    let data = readCards();
    /* De kaarten die de SERVER voor deze pagina rendert. In de paginastand zijn
       dit precies de zichtbare kaarten; al het nagehaalde blijft verborgen. */
    const pageEls = new Set(data.map(p => p.el));
    /* true zodra alle producten van de collectie in het raster staan. */
    let complete = pageCount <= 1;

    /* categorieën die ECHT op de pagina staan (uit de cat:-tags van de kaarten) */
    let present = [];
    /* Een categoriepagina = de collectie is zelf een categorie, d.w.z. haar handle
       komt terug als cat: op de kaarten (/collections/cobots -> cat:cobots). De
       all-view (alle-producten) matcht nooit. Vervangt de oude `!!CF[handle]`-test,
       die elke collectie buiten de zeven bekende als all-view behandelde. */
    let isCategoryPage = false;
    // a category page (cobots/grippers/…) always shows the Categorie block too (collapsed),
    // exactly like the design's activeKeys() which prepends 'category' to CF[k].
    let keys = DEFAULT_FILTERS;
    let catCollapsed = null; // design: ingeklapt op een categoriepagina, uitgeklapt op de all-view

    function refreshSchema() {
      present = [...new Set(data.map(p => p.category).filter(Boolean))];
      isCategoryPage = present.includes(handle);
      keys = isCategoryPage ? ['category', ...(CF[handle] || CF_DEFAULT)] : DEFAULT_FILTERS;
      if (catCollapsed === null) catCollapsed = isCategoryPage;
    }

    const filters = {
      categories: new Set(),
      brands: new Set(),
      ips: new Set(),
      payload: null, reach: null, price: null, // [min,max]
      q: (params.get('q') || '').trim().toLowerCase(),
    };

    const nlNum = (n) => new Intl.NumberFormat('nl-NL').format(n);
    const ipOf = (p) => { const s = (p.specs && p.specs['Beschermklasse']) || ''; const m = s.match(/IP\s?(\d{2})/i); return m ? 'IP' + m[1] : null; };
    const scoped = () => filters.categories.size ? data.filter(p => filters.categories.has(p.category)) : data;
    const bounds = (field, list) => { const v = list.map(p => p[field]).filter(x => x != null); return v.length ? [Math.min(...v), Math.max(...v)] : [0, 0]; };
    let B = {};

    /* Human label voor een cat:-key. Liquid levert er één per collectie in de
       shop (data-cat-labels); een cat:-tag zonder bijbehorende collectie valt
       terug op de vertitelde tag ("boosters-supports" -> "Boosters supports")
       in plaats van de kale handle. */
    const humanize = (k) => k.replace(/[-_]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
    const labelOf = (k) => catLabels[k] || humanize(k);

    /* categorieën aanwezig in de kaarten: eerst de zeven bekende in design-volgorde,
       daarna elke onbekende categorie (alfabetisch op label) — géén vaste lijst,
       zodat een nieuwe collectie/categorie vanzelf een chip krijgt. */
    function catList() {
      const known = CAT_ORDER.filter(k => present.includes(k));
      const extra = present
        .filter(k => !CAT_ORDER.includes(k))
        .sort((a, b) => labelOf(a).localeCompare(labelOf(b), 'nl'));
      return [...known, ...extra].map(k => ({ key: k, label: labelOf(k) }));
    }

    function recomputeBounds() {
      const s = scoped();
      B.payload = bounds('payloadKg', s);
      B.reach = bounds('reachMm', s);
      B.price = bounds('price', s);
      filters.payload = [...B.payload];
      filters.reach = [...B.reach];
      filters.price = [...B.price];
    }

    /* ---------- sidebar ---------- */
    /* Facet-labels komen uit merchant-data (collectietitels, product.vendor), dus
       escapen voor het attribuut/de tekst. De browser decodeert beide terug, dus
       cb.value blijft exact het ruwe label (waar de filters op matchen) en de
       gerenderde tekst is identiek aan voorheen. */
    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    /* `count === null` betekent: DIT WETEN WE NIET. Een facetaantal is een
       bewering over de HELE collectie; is die niet volledig binnengehaald, dan
       hoort er geen getal te staan. Een te laag getal is erger dan geen getal:
       het spreekt de teller tegen en het maakt van "Cobot-armen (18)" een
       belofte waar 16 cobots buiten vallen. */
    function checkboxRow(name, label, set, count) {
      const e = esc(label);
      const c = (count === null || count === undefined) ? '' : `<span class="count">${count}</span>`;
      return `<label class="fopt"><input type="checkbox" data-fkind="${name}" value="${e}" ${set.has(label) ? 'checked' : ''}><span>${e}</span>${c}</label>`;
    }
    function rangeBlock(key, title, unit, b, val) {
      const [lo, hi] = b; const [mn, mx] = val;
      if (lo === hi) return '';
      return `<div class="filter-block">
        <h4>${title}</h4>
        <div class="range" data-range="${key}" data-lo="${lo}" data-hi="${hi}">
          <div class="range__vals"><span data-rmin>${nlNum(mn)} ${unit}</span><span data-rmax>${nlNum(mx)} ${unit}</span></div>
          <div class="range__track"><div class="range__fill"></div>
            <input type="range" class="r-min" min="${lo}" max="${hi}" value="${mn}" step="any" aria-label="${title} minimum">
            <input type="range" class="r-max" min="${lo}" max="${hi}" value="${mx}" step="any" aria-label="${title} maximum">
          </div>
        </div>
      </div>`;
    }
    function build() {
      const s = scoped();
      /* Alleen tellen als de collectie compleet is. Zie checkboxRow(). */
      const showCounts = complete;
      const brandCounts = {}, ipCounts = {};
      s.forEach(p => { brandCounts[p.brand] = (brandCounts[p.brand] || 0) + 1; const ip = ipOf(p); if (ip) ipCounts[ip] = (ipCounts[ip] || 0) + 1; });
      const brandList = Object.keys(brandCounts).sort();
      const ipList = Object.keys(ipCounts).sort();
      const cats = catList();
      const catLabel = Object.fromEntries(cats.map(c => [c.key, c.label]));

      let html = '';
      // categorie (alleen op de all-view; standaard uitgeklapt zoals in het design)
      if (keys.includes('category')) {
        html += `<div class="filter-block filter-block--cat${catCollapsed ? ' collapsed' : ''}">
          <button type="button" class="filter-block__toggle" data-toggle-cat><h4>${T('collection.filter_category', 'Categorie')}</h4>
            <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 4l4 4 4-4"/></svg></button>
          <div class="filter-block__opts">` +
          cats.map(c => checkboxRow('category', c.label, new Set([...filters.categories].map(k => catLabel[k])), showCounts ? data.filter(p => p.category === c.key).length : null)).join('') + `</div></div>`;
      }
      if (keys.includes('brand') && brandList.length > 1) html += `<div class="filter-block"><h4>${T('collection.filter_brand', 'Merk')}</h4>` + brandList.map(b => checkboxRow('brand', b, filters.brands, showCounts ? brandCounts[b] : null)).join('') + `</div>`;
      if (keys.includes('payload')) html += rangeBlock('payload', 'Payload', 'kg', B.payload, filters.payload);
      if (keys.includes('reach')) html += rangeBlock('reach', T('collection.filter_reach', 'Reikwijdte'), 'mm', B.reach, filters.reach);
      if (keys.includes('ip') && ipList.length) html += `<div class="filter-block"><h4>${T('collection.filter_ip', 'Beschermklasse (IP)')}</h4>` + ipList.map(ip => checkboxRow('ip', ip, filters.ips, showCounts ? ipCounts[ip] : null)).join('') + `</div>`;
      if (keys.includes('price')) html += rangeBlock('price', T('collection.filter_price', 'Prijs'), '€', B.price, filters.price);

      const fb = scope.querySelector('#filterBody') || $('#filterBody');
      if (fb) { fb.innerHTML = html; fb.querySelectorAll('.range').forEach(initRange); }
    }

    /* ---------- dual range ---------- */
    function initRange(el) {
      const key = el.dataset.range, lo = +el.dataset.lo, hi = +el.dataset.hi;
      const rmin = el.querySelector('.r-min'), rmax = el.querySelector('.r-max');
      const fill = el.querySelector('.range__fill');
      const lblMin = el.querySelector('[data-rmin]'), lblMax = el.querySelector('[data-rmax]');
      const unit = key === 'price' ? '€' : key === 'payload' ? 'kg' : 'mm';
      function paint() {
        let a = +rmin.value, b = +rmax.value;
        if (a > b) { [a, b] = [b, a]; }
        const l = (a - lo) / (hi - lo) * 100, r = (b - lo) / (hi - lo) * 100;
        fill.style.left = l + '%'; fill.style.width = (r - l) + '%';
        lblMin.textContent = (key === 'price' ? '€ ' + nlNum(a) : nlNum(a) + ' ' + unit);
        lblMax.textContent = (key === 'price' ? '€ ' + nlNum(b) : nlNum(b) + ' ' + unit);
        filters[key] = [Math.min(a, b), Math.max(a, b)];
      }
      rmin.addEventListener('input', () => { paint(); apply(); });
      rmax.addEventListener('input', () => { paint(); apply(); });
      paint();
    }

    /* ---------- filtering ---------- */
    function rangeActive(key) { return B[key] && (filters[key][0] > B[key][0] || filters[key][1] < B[key][1]); }
    function matches(p) {
      if (filters.categories.size && !filters.categories.has(p.category)) return false;
      if (filters.brands.size && !filters.brands.has(p.brand)) return false;
      if (filters.ips.size && !filters.ips.has(ipOf(p))) return false;
      for (const key of ['payload', 'reach']) {
        if (rangeActive(key)) { const v = key === 'payload' ? p.payloadKg : p.reachMm; if (v == null || v < filters[key][0] || v > filters[key][1]) return false; }
      }
      if (B.price && (p.price < filters.price[0] || p.price > filters.price[1])) return false;
      if (filters.q && !(p.name + ' ' + p.brand).toLowerCase().includes(filters.q)) return false;
      return true;
    }
    const sortEl = () => scope.querySelector('#sortSelect') || $('#sortSelect');
    const sortValue = () => { const el = sortEl(); return el ? el.value : 'featured'; };
    function sortList(list) {
      const v = sortValue();
      const c = [...list];
      if (v === 'price-asc') c.sort((a, b) => a.price - b.price);
      else if (v === 'price-desc') c.sort((a, b) => b.price - a.price);
      else if (v === 'payload-desc') c.sort((a, b) => (b.payloadKg || 0) - (a.payloadKg || 0));
      else if (v === 'name') c.sort((a, b) => a.name.localeCompare(b.name));
      return c;
    }

    /* Er is pas iets te FILTEREN zodra de bezoeker zelf iets kiest. Zonder dat
       blijft de serverpagina staan (paginastand) en toont de teller het
       collectietotaal — precies wat een gepagineerde winkel hoort te doen. */
    function filtersActive() {
      return filters.categories.size > 0 || filters.brands.size > 0 || filters.ips.size > 0
        || rangeActive('payload') || rangeActive('reach') || rangeActive('price')
        || !!filters.q || sortValue() !== 'featured';
    }

    function chips() {
      const box = scope.querySelector('#activeFilters') || $('#activeFilters'); if (!box) return;
      const items = [];
      filters.brands.forEach(b => items.push(['brand', b, b]));
      filters.ips.forEach(ip => items.push(['ip', ip, ip]));
      if (rangeActive('payload')) items.push(['payload', null, `Payload ${nlNum(filters.payload[0])}–${nlNum(filters.payload[1])} kg`]);
      if (rangeActive('reach')) items.push(['reach', null, T('collection.chip_reach', 'Reik {from}–{to} mm').replace('{from}', nlNum(filters.reach[0])).replace('{to}', nlNum(filters.reach[1]))]);
      if (B.price && (filters.price[0] > B.price[0] || filters.price[1] < B.price[1])) items.push(['price', null, `€ ${nlNum(filters.price[0])}–${nlNum(filters.price[1])}`]);
      box.innerHTML = items.map(([k, v, lbl]) => `<span class="chip">${esc(lbl)}<button data-clear="${k}" data-val="${esc(v || '')}" aria-label="${T('common.remove', 'Verwijder')}">✕</button></span>`).join('')
        + (items.length ? `<button class="chip chip--clear" id="chipClearAll">${T('collection.clear_all', 'Alles wissen')}</button>` : '');
    }

    const pagerEl = () => scope.querySelector('[data-collection-pagination]') || $('[data-collection-pagination]');
    /* De paginatienav mag alleen weg als er ECHT niets meer te bladeren valt:
       alles staat in het raster én er wordt gefilterd. Is het nahalen mislukt,
       dan blijft hij staan — anders zou een product onbereikbaar worden. */
    function setPager(visible) {
      const nav = pagerEl();
      if (nav) nav.classList.toggle('hide', !visible);
    }

    function setCount(n) {
      const rc = scope.querySelector('#resCount') || $('#resCount');
      if (rc) rc.textContent = n;
    }

    /* De eerlijke melding uit main-collection.liquid (#collPartial). Hij komt
       PAS in beeld als het nahalen definitief mislukt is — niet terwijl het nog
       loopt, anders schreeuwt elke pagina even "onvolledig". */
    function setPartial(on) {
      const el = scope.querySelector('#collPartial') || $('#collPartial');
      if (el) el.classList.toggle('hide', !on);
    }

    function apply() {
      const empty = scope.querySelector('#emptyState') || $('#emptyState');
      if (!filtersActive() && pageCount > 1) {
        /* ---- paginastand: laat de serverpagina exact staan ---- */
        data.forEach(p => { if (p.el) p.el.classList.toggle('hide', !pageEls.has(p.el)); });
        setCount(Number.isFinite(serverTotal) ? serverTotal : data.length);
        if (empty) empty.classList.add('hide');
        grid.classList.remove('hide');
        setPager(true);
        setPartial(rendered && !complete);
        chips();
        return;
      }
      /* ---- volledige stand: alle treffers uit de hele collectie ---- */
      const list = sortList(data.filter(matches));
      setCount(list.length);
      // toon/verberg + herorden de kaarten; nagehaalde treffers worden hier pas
      // echt gemaakt, zodat de DOM alleen groeit voor wat iemand ook ziet.
      data.forEach(p => { if (p.el) p.el.classList.add('hide'); });
      freshEls = [];
      const frag = document.createDocumentFragment();
      list.forEach(p => { const el = elOf(p); if (!el) return; el.classList.remove('hide'); frag.appendChild(el); });
      grid.appendChild(frag);
      if (empty) empty.classList.toggle('hide', list.length > 0);
      grid.classList.toggle('hide', list.length === 0);
      setPager(!complete);
      setPartial(rendered && !complete);
      chips();
      if (freshEls.length) { freshEls = []; afterNewCards(); }
    }

    /* Nieuwe kaarten in het raster: bedragen schilderen (PRICE RULE — de server
       heeft al bepaald wát er mag staan) en de vergelijker opnieuw laten
       indexeren, anders levert "Vergelijk" op een nagehaalde cobot een lege rij.
       Het herinitialiseren loopt via de registratie die olmia-compare.js zelf bij
       olmia-editor.js achterlaat; geen private functie van buitenaf. */
    function afterNewCards() {
      try { if (window.OlmiaUI && OlmiaUI.paintPrices) OlmiaUI.paintPrices(grid); } catch (e) { console.error(e); }
      try {
        const reg = (window.OlmiaEditor && window.OlmiaEditor._registry) || [];
        reg.filter(e => e && e.key === 'olmia-compare')
           .forEach(e => { try { if (e.teardown) e.teardown(scope); if (e.init) e.init(scope); } catch (err) { console.error(err); } });
      } catch (e) { console.error(e); }
    }

    /* ---------- events ---------- */
    function bind() {
      // Bind once per section root: a re-render gives a fresh root (rebinds the new
      // nodes); a same-root re-init is a no-op (no stacked delegated handlers).
      if (root.dataset.filtersBound === '1') return;
      root.dataset.filtersBound = '1';
      const filterBody = scope.querySelector('#filterBody') || $('#filterBody');
      filterBody.addEventListener('click', (e) => {
        if (e.target.closest('[data-toggle-cat]')) { catCollapsed = !catCollapsed; scope.querySelector('.filter-block--cat')?.classList.toggle('collapsed', catCollapsed); }
      });
      filterBody.addEventListener('change', (e) => {
        const cb = e.target.closest('input[type=checkbox]'); if (!cb) return;
        const kind = cb.dataset.fkind, val = cb.value;
        if (kind === 'category') {
          const key = catList().find(c => c.label === val)?.key;
          if (cb.checked) filters.categories.add(key); else filters.categories.delete(key);
          // filterset kan wijzigen: reset niet-geldige filters
          filters.ips.clear();
          recomputeBounds(); build(); apply();
        } else if (kind === 'brand') { cb.checked ? filters.brands.add(val) : filters.brands.delete(val); apply(); }
        else if (kind === 'ip') { cb.checked ? filters.ips.add(val) : filters.ips.delete(val); apply(); }
      });
      sortEl()?.addEventListener('change', apply);
      // live zoeken binnen de collectie (prefill uit ?q=)
      const cs = scope.querySelector('#collSearch') || $('#collSearch');
      if (cs) {
        cs.value = filters.q || '';
        cs.addEventListener('input', () => { filters.q = cs.value.trim().toLowerCase(); apply(); });
      }
      (scope.querySelector('#activeFilters') || $('#activeFilters')).addEventListener('click', (e) => {
        const c = e.target.closest('[data-clear]'); if (c) { clear(c.dataset.clear, c.dataset.val); return; }
        if (e.target.id === 'chipClearAll') resetAll();
      });
      (scope.querySelector('#filterReset') || $('#filterReset')).addEventListener('click', resetAll);
      const openF = () => { $('#filters').classList.add('open'); $('#filterScrim').classList.add('open'); document.body.style.overflow = 'hidden'; $('#filterClose').style.display = 'grid'; };
      const closeF = () => { $('#filters').classList.remove('open'); $('#filterScrim').classList.remove('open'); document.body.style.overflow = ''; };
      (scope.querySelector('#filterTrigger') || $('#filterTrigger')).addEventListener('click', openF);
      (scope.querySelector('#filterClose') || $('#filterClose')).addEventListener('click', closeF);
      (scope.querySelector('#filterScrim') || $('#filterScrim')).addEventListener('click', closeF);
    }
    function clear(kind, val) {
      if (kind === 'brand') filters.brands.delete(val);
      else if (kind === 'ip') filters.ips.delete(val);
      else if (kind === 'payload') filters.payload = [...B.payload];
      else if (kind === 'reach') filters.reach = [...B.reach];
      else if (kind === 'price') filters.price = [...B.price];
      build(); apply();
    }
    function resetAll() {
      filters.categories.clear(); filters.brands.clear(); filters.ips.clear(); filters.q = '';
      const cs = scope.querySelector('#collSearch') || $('#collSearch'); if (cs) cs.value = '';
      recomputeBounds(); build(); apply();
    }

    /* ---------------- hydratie: de rest van de collectie erbij ---------------- */

    /* URL voor pagina p van dezelfde collectieweergave. location.pathname houdt
       een eventueel tag-pad (/collections/x/cat:cobots) intact. */
    function pageUrl(p, asSection) {
      const qs = new URLSearchParams(location.search);
      qs.delete('page');
      qs.set('page', String(p));
      if (asSection && sectionId) qs.set('sections', sectionId); else qs.delete('sections');
      return location.pathname + '?' + qs.toString();
    }

    /* Haalt de kaart-HTML van één pagina op. Eerst via de Section Rendering API
       (klein antwoord: alleen deze sectie); lukt dat niet, dan het gewone
       document als terugval. Er wordt NOOIT script uit het antwoord uitgevoerd:
       DOMParser levert een inert document en alleen .pcard-knopen worden
       overgenomen. */
    async function fetchPage(p, signal) {
      const key = pageUrl(p, true);
      if (PAGE_CACHE.has(key)) return PAGE_CACHE.get(key);
      let html = '';
      if (sectionId) {
        try {
          const res = await fetch(key, { signal, credentials: 'same-origin', priority: 'low' });
          if (res.ok) {
            const json = await res.json();
            html = (json && typeof json[sectionId] === 'string') ? json[sectionId] : '';
          }
        } catch (e) { if (e && e.name === 'AbortError') throw e; html = ''; }
      }
      if (!html) {
        const res = await fetch(pageUrl(p, false), { signal, credentials: 'same-origin', priority: 'low' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        html = await res.text();
      }
      /* DOMParser levert een INERT document: geen script draait, geen foto wordt
         opgehaald. Alleen de .pcard-knopen worden eruit gelezen. */
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = [...doc.querySelectorAll('.pcard')].map(el => {
        /* Elke serverpagina zet zijn eerste rij op loading="eager" (die kaart is
           dáár het LCP-element). Een nagehaalde kaart is dat nooit, dus die hint
           moet eraf — anders trekt pagina 2 straks foto's binnen die boven aan
           pagina 1 niets te zoeken hebben. */
        el.querySelectorAll('img').forEach(img => {
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
        });
        el.classList.add('hide');
        const { product, specs } = parseCard(el);
        return { html: el.outerHTML, product, specs };
      });
      PAGE_CACHE.set(key, cards);
      return cards;
    }

    /* Zijbalk + raster klaarzetten over de producten die op dít moment bekend
       zijn. Mag meer dan eens: het vangnet hieronder rendert desnoods met een
       halve collectie, en zodra het nahalen alsnog rond is rendert de hydratie
       er overheen. */
    let rendered = false;
    function render() {
      rendered = true;
      refreshSchema();
      recomputeBounds();
      build();
      apply();
    }

    async function hydrate() {
      if (complete) return;
      if (pageCount > MAX_HYDRATE_PAGES) { render(); return; }
      const ac = new AbortController();
      RUNNING.set(root, ac);
      const wanted = [];
      for (let p = 1; p <= pageCount; p++) if (p !== curPage) wanted.push(p);
      /* Gefaalde pagina's worden VERZAMELD, niet weggemoffeld. Vóór 2026-08-20
         werd een mislukte pagina simpelweg uit `all` gelaten en draaide render()
         gewoon door: gemeten (pagina 5 van 8 laten falen) bleef de teller op 177
         staan terwijl Cobot-armen van 34 naar 18 zakte en Service & kits van 42
         naar 35 — de som van de facetten was 153. De pagina sprak zichzelf tegen
         en "Cobot-armen (18)" maakte 16 cobots onbereikbaar. */
      const failed = [];
      /* Maximaal 3 tegelijk: genoeg om de rondreistijd te verbergen, weinig
         genoeg om niet met de eerste weergave te concurreren. */
      const queue = wanted.slice();
      const fetched = new Map();
      async function worker(list) {
        while (list.length) {
          const p = list.shift();
          try {
            fetched.set(p, await fetchPage(p, ac.signal));
          } catch (e) {
            if (e && e.name === 'AbortError') return;
            failed.push(p);
          }
        }
      }
      await Promise.all([worker(queue), worker(queue), worker(queue)]);
      if (ac.signal.aborted) return;

      /* HERKANSING — één keer, serieel, na een pauze. Een 429 of een
         weggevallen verbinding is bijna altijd tijdelijk; een tweede ronde is
         goedkoper dan een pagina die de rest van het bezoek onvolledig blijft.
         Meer dan één herkansing niet: dan is er iets structureel mis en is
         zwijgen over de aantallen het juiste antwoord. */
      if (failed.length) {
        await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
        if (ac.signal.aborted) return;
        const again = failed.splice(0, failed.length);
        await worker(again);
        if (ac.signal.aborted) return;
      }
      /* Compleet = ELKE gewenste pagina is binnen. Niet "er ging niets stuk". */
      const ok = failed.length === 0 && fetched.size === wanted.length;
      RUNNING.delete(root);
      /* De volledige collectie, in collectievolgorde: de serverpagina houdt zijn
         eigen kaarten (dezelfde DOM-knopen, dus niets verspringt), de rest komt
         er als gegeven omheen te staan. */
      const all = [];
      for (let p = 1; p <= pageCount; p++) {
        if (p === curPage) { all.push(...data.filter(r => r.el && pageEls.has(r.el))); continue; }
        const got = fetched.get(p);
        if (got) all.push(...got.map(c => record(null, c.product, c.specs, c.html)));
      }
      data = all;
      complete = ok;
      render();
    }

    /* ------------------------------- start -------------------------------- */
    refreshSchema();
    recomputeBounds();
    bind();
    if (complete) {
      // Eén pagina: alles staat er al, geen verzoek, geen wachttijd.
      render();
    } else {
      // De teller staat serverzijdig al goed; het raster toont de serverpagina.
      // De zijbalk verschijnt zodra de facetaantallen over de HELE collectie
      // gaan — liever een fractie later dan meteen met verkeerde getallen.
      apply();
      hydrate();
      // Vangnet: laat de filters nooit uitblijven als het netwerk hangt. Loopt
      // het nahalen daarna alsnog binnen, dan rendert hydrate() er overheen.
      setTimeout(() => { if (!rendered) render(); }, 8000);
    }
  }

  function teardown(sectionRoot) {
    const scope = sectionRoot || document;
    // Zelfde wortelbepaling als init(): via het raster, niet via de eerste
    // [data-collection] (dat is op merkpagina's de hero).
    const grid = scope.querySelector && scope.querySelector('#grid');
    const root = grid && grid.closest('[data-collection]');
    const ac = root && RUNNING.get(root);
    if (ac) { try { ac.abort(); } catch (e) { /* niets */ } RUNNING.delete(root); }
  }

  if (window.OlmiaEditor) OlmiaEditor.register({
    key: 'olmia-filters',
    match: (r) => !!(r && r.querySelector && r.querySelector('#grid') && r.querySelector('[data-collection]')),
    init,
    teardown
  });

  if (document.readyState !== 'loading') init(document);
  else document.addEventListener('DOMContentLoaded', () => init(document));
})();
